/**
 * The reviewer's full details for one ISFR request as a Markdown document, for
 * the Reviewer Breakdown tab's download. Everything the review view shows, in
 * one readable file: the decision and counts, the executive summary, the
 * reviewer reasons and actions, the flags, and every check in every category
 * with its details, what it worked from and concluded, and the quotes it cited.
 * It also carries the engine detail the page leaves out, such as routing codes,
 * labelled as such. Check names only, never check ids. Pure, so it is
 * unit-tested.
 */
import { describeEvidence, internalEvidence } from './checkEvidence';
import {
  NOT_APPLICABLE_TEXT, categoryStatus, checkName, checkStatus,
} from './checkDisplay';
import { describeCounts, groupCategoryChecks, readIsfrRecord } from './reviewRecord';
import { routingNote } from './reviewBlock';

const cell = (text) => String(text).replace(/\|/g, '\\|').replace(/\n+/g, ' ');

const bullets = (items) => items.map((item) => `- ${String(item).replace(/\n+/g, ' ')}`);

/** describeEvidence items as Markdown lines. */
export function evidenceToMarkdown(items) {
  const lines = [];
  items.forEach((item) => {
    switch (item.kind) {
      case 'value':
        lines.push(`- **${item.label}:** ${item.text}`);
        break;
      case 'list':
        lines.push(`- **${item.label}:**`, ...item.items.map((text) => `  - ${text}`));
        break;
      case 'paragraph':
        lines.push('', `**${item.label}:**`, '', item.text, '');
        break;
      case 'table':
        lines.push(
          '', `**${item.label}:**`, '',
          `| ${item.columns.map((c) => cell(c.title)).join(' | ')} |`,
          `| ${item.columns.map(() => '---').join(' | ')} |`,
          ...item.rows.map((row) => `| ${item.columns.map((c) => cell(row[c.key] ?? '')).join(' | ')} |`),
          '',
        );
        break;
      case 'criteria':
        lines.push('', `**${item.label}:**`, '');
        item.criteria.forEach((c) => {
          lines.push(`- **${c.label}**${c.score !== null ? ` (${c.score} / 100)` : ''}${c.assessment ? `: ${c.assessment}` : ''}`);
          if (c.planStatus || c.planQuote) lines.push(`  - ${[c.planStatus, c.planQuote && `"${c.planQuote}"`].filter(Boolean).join(': ')}`);
          if (c.recheck && (c.recheck.status || c.recheck.quote)) {
            lines.push(`  - Rechecked against the plan alone: ${[c.recheck.status, c.recheck.quote && `"${c.recheck.quote}"`].filter(Boolean).join(': ')}`);
          }
          if (c.facts.length > 0) lines.push(`  - ${c.facts.map((f) => `${f.label}: ${f.text}`).join('; ')}`);
        });
        lines.push('');
        break;
      default:
        break;
    }
  });
  return lines;
}

const checkSection = (check, category, nameOf, statusText) => {
  const status = statusText || checkStatus(check.result, category.role, category.advisory).text;
  const lines = [`#### ${checkName(check)}: ${status}`, ''];
  if (check.details) lines.push(check.details, '');
  lines.push(...evidenceToMarkdown(describeEvidence(check.id, check.evidence, check.details, nameOf)));
  const internal = internalEvidence(check.id, check.evidence);
  if (internal.length > 0) {
    lines.push('', 'Engine detail (not shown on the review page):', '');
    internal.forEach(({ key, value }) => lines.push(`- \`${key}\`: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`));
  }
  if (check.evidenceQuotes && check.evidenceQuotes.length > 0) {
    lines.push('', 'Cited from the submission by the agent, not yet checked against the submission:', '');
    check.evidenceQuotes.forEach((quote) => lines.push(`> ${quote.replace(/\n+/g, ' ')}`, '>'));
    lines.pop();
  }
  lines.push('');
  return lines;
};

export function buildReviewerReport(record) {
  const view = readIsfrRecord(record);
  const frId = (record && (record.apfp_fundrequestid || record.id)) || 'Unknown request';
  const nameOf = (id) => (view.checks.byId.has(id) ? checkName(view.checks.byId.get(id)) : 'A check not shown on this record');
  const lines = [`# Reviewer details: ${frId}`, ''];

  lines.push(`- **Final decision:** ${view.verdictLabel || view.rawVerdict || 'No decision recorded'}`);
  const routing = routingNote(view);
  if (routing) lines.push(`- **${routing.label}:** ${routing.text}`);
  if (view.counts) {
    const text = describeCounts(view.counts);
    lines.push(`- **Validations performed:** ${[`${text.passed}.`, text.attention, text.notAutomated, text.worthVerifying].filter(Boolean).join(' ')}`);
  }
  if (record && record.processed_date) lines.push(`- **Validated:** ${record.processed_date}`);
  if (view.checks.reviewerComment) lines.push(`- **Reviewer note:** ${view.checks.reviewerComment}`);
  lines.push('');

  const { errored, unhandledFails } = view.incomplete;
  if (errored.value.length > 0) {
    lines.push('## Did not complete', '', ...bullets(errored.value.map((c) => `${checkName(c)}${c.message ? `: ${c.message}` : ''}`)), '');
  }
  if (unhandledFails.value.length > 0) {
    lines.push('## Failed with no specific reason', '', ...bullets(unhandledFails.value.map((c) => `${checkName(c)}${c.message ? `: ${c.message}` : ''}`)), '');
  }

  lines.push('## Executive summary', '', view.narrative || 'No executive summary was recorded for this run.', '');

  const lane = (title, read) => {
    lines.push(`## ${title}`, '');
    if (read.status === 'ok' && read.value.length > 0) lines.push(...bullets(read.value));
    else lines.push(read.status === 'unreadable' ? 'Could not be read on this record.' : 'None recorded.');
    lines.push('');
  };
  lane('Reasons', view.reviewerReasons);
  lane('Actions', view.reviewerActions);

  lines.push('## Flags to keep in mind', '', 'Not hard blockers. They did not change the agent\'s decision, but each is worth your attention.', '');
  if (view.checks.status !== 'ok') lines.push('Flag detail is not saved for this request.');
  else if (view.flags.length === 0) lines.push('No flags triggered.');
  else lines.push(...bullets(view.flags.map((f) => `${checkName(f)} (relates to ${f.categoryLabel})${f.details ? `: ${f.details}` : ''}`)));
  lines.push('');

  view.rollup.categories.forEach((category) => {
    lines.push(`## ${category.label}: ${categoryStatus(category).text}`, '');
    if (view.checks.status !== 'ok') {
      lines.push('Check-by-check results are not saved for this request.', '');
      return;
    }
    const groups = groupCategoryChecks(category, view.checks.byId);
    [...groups.failed, ...groups.other, ...groups.incomplete, ...groups.assisting, ...groups.flagged, ...groups.passed]
      .forEach((check) => lines.push(...checkSection(check, category, nameOf)));
    groups.notApplicable.forEach((check) => lines.push(...checkSection(check, category, nameOf, NOT_APPLICABLE_TEXT)));
    if (groups.missing.length > 0) {
      lines.push(`${groups.missing.length === 1 ? 'One check' : `${groups.missing.length} checks`} in this category have no stored result.`, '');
    }
  });

  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()}\n`;
}

export const reviewerReportFilename = (record) =>
  `${(record && (record.apfp_fundrequestid || record.id)) || 'isfr-request'}-reviewer-details.md`;
